import type { TodayFavouriteCandidate } from "@learn-flow/contracts";

const favouritesStorageKey = "learnflow.today.favourites.v1";
const favouritesLimit = 4;
let inMemoryIds: string[] = [];

export function selectFavourites(candidates: TodayFavouriteCandidate[]): TodayFavouriteCandidate[] {
  const byId = new Map(candidates.map((candidate) => [candidate.activityId, candidate]));
  let storedIds: string[] = inMemoryIds;
  try {
    const stored = window.sessionStorage.getItem(favouritesStorageKey);
    const parsed: unknown = stored ? JSON.parse(stored) : [];
    if (Array.isArray(parsed)) {
      storedIds = parsed.filter((id): id is string => typeof id === "string");
    }
  } catch {
    storedIds = inMemoryIds;
  }

  const selected: TodayFavouriteCandidate[] = [];
  const restoredIds = new Set<string>();
  for (const id of storedIds) {
    const candidate = byId.get(id);
    if (!candidate || restoredIds.has(id)) continue;
    selected.push(candidate);
    restoredIds.add(id);
    if (selected.length >= favouritesLimit) break;
  }
  const selectedIds = new Set(selected.map((candidate) => candidate.activityId));
  const highRated = shuffled(
    candidates.filter(
      (candidate) =>
        candidate.interest !== null &&
        candidate.interest >= 3 &&
        !selectedIds.has(candidate.activityId),
    ),
  );
  const backfill = shuffled(
    candidates.filter(
      (candidate) =>
        (candidate.interest === null || candidate.interest < 3) &&
        !selectedIds.has(candidate.activityId),
    ),
  );
  for (const candidate of [...highRated, ...backfill]) {
    if (selected.length >= favouritesLimit) break;
    if (selectedIds.has(candidate.activityId)) continue;
    selected.push(candidate);
    selectedIds.add(candidate.activityId);
  }
  try {
    window.sessionStorage.setItem(
      favouritesStorageKey,
      JSON.stringify(selected.map((candidate) => candidate.activityId)),
    );
  } catch {
    // A privacy-restricted browser may deny session storage; the view remains usable.
  }
  inMemoryIds = selected.map((candidate) => candidate.activityId);
  return selected;
}

function shuffled<T>(values: T[]): T[] {
  const result = [...values];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const random = new Uint32Array(1);
    crypto.getRandomValues(random);
    const target = (random[0] ?? 0) % (index + 1);
    [result[index], result[target]] = [result[target] as T, result[index] as T];
  }
  return result;
}
